import React, { Ref, RefObject } from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { ChartContainer } from './ColumnChart.styles';
import {
  DECIMAL_TYPES,
  ICurrencyFormat,
  IDisplayByType,
} from '../../utils/types';
import { Chart } from 'highcharts';
import { DECIMALS_FORMATS, DISPLAY_BY } from 'utils/constants';
import {
  currencyLabelFormatter,
  getColorIndex,
  getFormattedPercentage,
  tooltipValueFormatter,
} from 'utils/format';
import {
  TooltipColor,
  TooltipContainer,
  TooltipItem,
  TooltipItemName,
  TooltipItemValue,
} from 'components/BarChart/BarChart.styles';
import { colors } from '@alixpartners/ui-components';
import { renderToString } from 'react-dom/server';

type Serie = {
  data: Array<Number>;
  name?: string;
};

export interface IColumnChartProps {
  categories: string[];
  chartRef?: Ref<{ chart: Chart; container: RefObject<HTMLDivElement> }>;
  displayBy: IDisplayByType;
  format: ICurrencyFormat;
  series: Serie[];
}
const COLORS = ['#032E45', '#0696A6'];

export const ColumnChart = ({
  categories,
  chartRef,
  displayBy,
  format,
  series: seriesRaw,
}: IColumnChartProps) => {
  let maxDecimal = DECIMALS_FORMATS[format.decimal];
  const isPercentage = displayBy === DISPLAY_BY.PERCENTAGE_OF_REVENUE;
  const isNumber = displayBy === DISPLAY_BY.HEADCOUNT;
  const series = seriesRaw.map((serie, index) => ({
    ...serie,
    color: COLORS[getColorIndex(serie.name || '')],
    marker: {
      symbol: 'square',
    },
  }));
  if (isPercentage) {
    const maxSerieValue: number = Math.max.apply(
      null,
      series.map((serie) => Math.max.apply(null, serie.data as number[]))
    );
    maxDecimal = maxSerieValue < 10 ? '.0000' : maxDecimal;
  }
  const chartOptions = {
    chart: {
      type: 'column',
      spacingTop: 40,
      spacingBottom: 20,
    },
    title: {
      text: null,
    },
    xAxis: {
      categories,
      labels: {
        autoRotation: undefined,
        y: 40,
      },
    },
    legend: {
      enabled: false,
    },
    yAxis: {
      min: 0,
      height: '100%',
      title: {
        text: null,
      },
    },
    credits: { enabled: false },
    exporting: { enabled: false },
    tooltip: {
      className: 'column-chart-tooltip',
      shared: true,
      useHTML: true,
      outside: true,
      backgroundColor: colors.white,
      borderWidth: 0,
      borderRadius: 2,
      formatter: function () {
        // @ts-ignore
        const { points } = this;
        const isOnlySerie = points.length === 1;
        return renderToString(
          <TooltipContainer>
            {points.map(
              (point: {
                series: { name: string };
                colorIndex: number;
                y: number;
                x: number;
              }) =>
                point ? (
                  <TooltipItem key={`${point.series.name}-${point.colorIndex}`}>
                    <span>
                      <TooltipColor
                        colorIndex={getColorIndex(point.series.name)}
                      />

                      <TooltipItemName>
                        {isOnlySerie ? point.x : point.series.name}
                      </TooltipItemName>
                    </span>
                    <TooltipItemValue>
                      {tooltipValueFormatter({
                        value: point.y,
                        format,
                        displayType: displayBy,
                        maxDecimal,
                      })}
                    </TooltipItemValue>
                  </TooltipItem>
                ) : null
            )}
          </TooltipContainer>
        );
      },
    },
    plotOptions: {
      series: {
        animation: false,
        dataLabels: {
          enabled: true,
          verticalAlign: 'top',
          y: -24,
          // @ts-ignore
          formatter: function () {
            if (isPercentage) {
              return getFormattedPercentage(this.y, {
                ...format,
                decimal: maxDecimal as keyof DECIMAL_TYPES,
              });
            }
            if (isNumber) {
              // @ts-ignore
              return this.y;
            }
            return currencyLabelFormatter(this.y, format);
          },
          style: {
            color: 'black',
            textOutline: '0px contrast',
            'stroke-width': 0,
          },
        },
      },
    },
    series,
  };
  return (
    <ChartContainer>
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
        ref={chartRef}
      />
    </ChartContainer>
  );
};

export default ColumnChart;
